import React from 'react';
import { Flex, Text } from '@chatsdet/ui';
import { LinkSimple, Paperclip, X } from '@phosphor-icons/react';
import { ContextUploadZone } from '../../context-library/components/ContextUploadZone';
import type { ContextDocument } from '../useExploratoryTests';

type ContextLibraryFieldProps = {
  /** Currently attached context documents (controlled). */
  value: ContextDocument[];
  onChange: (docs: ContextDocument[]) => void;
};

/**
 * Context section for the exploratory-test creation modal: add files and links
 * that become planning reading material (the "knowledge base" the agent draws
 * on). Uploads land in the org context library immediately (via the shared
 * {@link ContextUploadZone}); their ids are attached to the minted parent test
 * on submit. Distinct from runtime attachments — context never stages a fixture
 * into the test itself.
 */
export function ContextLibraryField({ value, onChange }: ContextLibraryFieldProps) {
  const addDocs = React.useCallback(
    (docs: ContextDocument[]) => {
      const existing = new Set(value.map((d) => d.id));
      const merged = [...value, ...docs.filter((d) => !existing.has(d.id))];
      onChange(merged);
    },
    [onChange, value]
  );

  const removeDoc = React.useCallback(
    (id: string) => onChange(value.filter((d) => d.id !== id)),
    [onChange, value]
  );

  return (
    <div className="space-y-2">
      <Text size="sm" className="font-medium">
        Context (optional)
      </Text>
      <Text size="xs" className="text-muted-foreground">
        Reference docs and links the agent reads while planning — specs, designs, runbooks. Saved to
        this test, not the individual run.
      </Text>

      <ContextUploadZone onAdded={addDocs} />

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((doc) => (
            <li
              key={doc.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-background px-2 py-1"
            >
              <Flex align="center" gap="sm" className="min-w-0">
                {doc.fileType === 'link' ? (
                  <LinkSimple size={14} className="shrink-0 text-muted-foreground" />
                ) : (
                  <Paperclip size={14} className="shrink-0 text-muted-foreground" />
                )}
                <Text size="xs" className="truncate">
                  {doc.fileName}
                </Text>
              </Flex>
              <button
                type="button"
                aria-label={`Remove ${doc.fileName}`}
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => removeDoc(doc.id)}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
